/**
 * Plain-data quaternion math — the kernel's foundational vocabulary.
 * Depends on: math/vec3. Depended on by: every kernel op (frame-remap, swing-twist, solve).
 *
 * Convention: right-handed, {x,y,z,w} with w the scalar part, matching glTF
 * and the Gen-2 engine the math is harvested from. All functions are pure;
 * none mutate their inputs.
 */
import { vec3Normalize, type Vec3Data } from './vec3.js';

export interface Quat {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly w: number;
}

export const QUAT_IDENTITY: Quat = Object.freeze({ x: 0, y: 0, z: 0, w: 1 });

export function quat(x: number, y: number, z: number, w: number): Quat {
  return { x, y, z, w };
}

/** Hamilton product a·b (apply b's rotation, then a's). */
export function quatMultiply(a: Quat, b: Quat): Quat {
  return {
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  };
}

export function quatConjugate(q: Quat): Quat {
  return { x: -q.x, y: -q.y, z: -q.z, w: q.w };
}

export function quatLengthSq(q: Quat): number {
  return q.x * q.x + q.y * q.y + q.z * q.z + q.w * q.w;
}

export function quatNormalize(q: Quat): Quat {
  const len = Math.sqrt(quatLengthSq(q));
  if (len === 0) {
    // A zero quaternion has no direction — normalizing it silently to identity
    // would be a masked data error (fail-loud contract, ADR-001 §7).
    throw new RangeError('quatNormalize: zero-length quaternion has no orientation');
  }
  return { x: q.x / len, y: q.y / len, z: q.z / len, w: q.w / len };
}

/** Inverse of a UNIT quaternion (the conjugate). Callers own the unit invariant. */
export function quatInvertUnit(q: Quat): Quat {
  return quatConjugate(q);
}

export function quatDot(a: Quat, b: Quat): number {
  return a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
}

/**
 * Ensure continuity with a previous sample: q and −q encode the same rotation,
 * but interpolating across the sign flip unwinds through the long way — the
 * Gen-2 "quaternion continuity flip" lesson, kept as a primitive.
 */
export function quatContinuous(q: Quat, prev: Quat): Quat {
  return quatDot(q, prev) < 0 ? { x: -q.x, y: -q.y, z: -q.z, w: -q.w } : q;
}

/**
 * Conjugation (sandwich) remap: R⁻¹ · δ · R, mapping a delta expressed in one
 * bone-local frame into another. THE load-bearing Gen-2 lesson — the previous
 * bug was `δ · R` (see research digest §2, RetargetEngine.js:720-759).
 */
export function quatConjugateBy(delta: Quat, frameMap: Quat): Quat {
  return quatMultiply(quatMultiply(quatInvertUnit(frameMap), delta), frameMap);
}

/** Angular difference in radians between two unit quaternions. */
export function quatAngleBetween(a: Quat, b: Quat): number {
  const d = Math.min(1, Math.abs(quatDot(a, b)));
  return 2 * Math.acos(d);
}

/**
 * Shortest-path spherical interpolation between UNIT quaternions (callers own
 * the unit invariant). The dot-sign flip takes the short arc; near-parallel
 * inputs fall back to normalized lerp (the sin denominator vanishes there and
 * nlerp is exact in the limit). Needed by the corrective bake pass, which must
 * reproduce the ENGINE's between-key interpolation of a driver track before
 * evaluating the nonlinear θ extraction (ADR-028; Codex P2 #103).
 */
export function quatSlerp(a: Quat, b: Quat, t: number): Quat {
  let bx = b.x;
  let by = b.y;
  let bz = b.z;
  let bw = b.w;
  let d = quatDot(a, b);
  if (d < 0) {
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
    d = -d;
  }
  if (d > 0.9995) {
    return quatNormalize({
      x: a.x + (bx - a.x) * t,
      y: a.y + (by - a.y) * t,
      z: a.z + (bz - a.z) * t,
      w: a.w + (bw - a.w) * t,
    });
  }
  const theta = Math.acos(Math.min(1, d));
  const s = Math.sin(theta);
  const wa = Math.sin((1 - t) * theta) / s;
  const wb = Math.sin(t * theta) / s;
  return { x: a.x * wa + bx * wb, y: a.y * wa + by * wb, z: a.z * wa + bz * wb, w: a.w * wa + bw * wb };
}

/** Unit quaternion for a rotation of `radians` about `axis` (normalized here, loudly). */
export function quatFromAxisAngle(axis: Vec3Data, radians: number): Quat {
  const u = vec3Normalize(axis);
  const s = Math.sin(radians / 2);
  return { x: u.x * s, y: u.y * s, z: u.z * s, w: Math.cos(radians / 2) };
}

/** Rotate a vector by a UNIT quaternion (callers own the unit invariant). */
export function quatRotateVec3(q: Quat, v: Vec3Data): Vec3Data {
  // v' = v + 2·(u × (u × v + w·v)) with u the vector part — the standard
  // expansion of q·v·q⁻¹ without constructing the matrix.
  const ux = q.x;
  const uy = q.y;
  const uz = q.z;
  const cx = uy * v.z - uz * v.y + q.w * v.x;
  const cy = uz * v.x - ux * v.z + q.w * v.y;
  const cz = ux * v.y - uy * v.x + q.w * v.z;
  return {
    x: v.x + 2 * (uy * cz - uz * cy),
    y: v.y + 2 * (uz * cx - ux * cz),
    z: v.z + 2 * (ux * cy - uy * cx),
  };
}

/**
 * Rotation quaternion from an ORTHONORMAL, RIGHT-HANDED column basis — the
 * rotation R whose matrix has COLUMNS x|y|z, so R·(1,0,0)=x, R·(0,1,0)=y,
 * R·(0,0,1)=z. It is the inverse of reading a bone's local axes off its
 * world-bind rotation, and the constructive core of the Hand Studio graft
 * (hands/graft-digits): it turns an anatomical frame — primary along the bone,
 * secondary the dorsal reference, their cross the curl axis — into the bone's
 * worldBind.rotation, whose inverse then maps that frame back to the canonical
 * (1,0,0)/(0,1,0) local axes the reorientation ladder's authored tier reads.
 *
 * Shepperd's method (the numerically-stable matrix→quaternion branch on the
 * largest pivot — avoids catastrophic cancellation when the trace is negative).
 * Callers own the orthonormal + right-handed invariant: a skew or left-handed
 * basis names no rotation and is a caller bug, not repaired here (the graft
 * builds z = normalize(x × y), so it is right-handed by construction). The
 * result is renormalized, loudly (quatNormalize throws on the impossible zero).
 */
export function quatFromBasis(x: Vec3Data, y: Vec3Data, z: Vec3Data): Quat {
  // Column-major placement: column 0 = x, column 1 = y, column 2 = z, so
  // m[row][col] reads m00=x.x m01=y.x m02=z.x / m10=x.y … (see the 4·w etc. below).
  const m00 = x.x;
  const m10 = x.y;
  const m20 = x.z;
  const m01 = y.x;
  const m11 = y.y;
  const m21 = y.z;
  const m02 = z.x;
  const m12 = z.y;
  const m22 = z.z;
  const trace = m00 + m11 + m22;
  let out: Quat;
  if (trace > 0) {
    const s = Math.sqrt(trace + 1) * 2; // s = 4·w
    out = { x: (m21 - m12) / s, y: (m02 - m20) / s, z: (m10 - m01) / s, w: 0.25 * s };
  } else if (m00 > m11 && m00 > m22) {
    const s = Math.sqrt(1 + m00 - m11 - m22) * 2; // s = 4·x
    out = { x: 0.25 * s, y: (m01 + m10) / s, z: (m02 + m20) / s, w: (m21 - m12) / s };
  } else if (m11 > m22) {
    const s = Math.sqrt(1 + m11 - m00 - m22) * 2; // s = 4·y
    out = { x: (m01 + m10) / s, y: 0.25 * s, z: (m12 + m21) / s, w: (m02 - m20) / s };
  } else {
    const s = Math.sqrt(1 + m22 - m00 - m11) * 2; // s = 4·z
    out = { x: (m02 + m20) / s, y: (m12 + m21) / s, z: 0.25 * s, w: (m10 - m01) / s };
  }
  return quatNormalize(out);
}
